import React, { useEffect, useId, useRef } from "react";
import type { PremiumCelebration } from "../../dataLayer/DataLayer";
import { Close } from "./icons";
import { TierStar } from "./tierStar";
import "./membershipCelebration.css";

function tierLabel(tier: PremiumCelebration["tier"]): string {
  return `${tier[0].toUpperCase()}${tier.slice(1)}`;
}

function tierRank(tier: PremiumCelebration["tier"]): number {
  return tier === "gold" ? 3 : tier === "silver" ? 2 : 1;
}

export function MembershipCelebration({
  celebration,
  complimentaryTier,
  onClose,
}: {
  celebration: PremiumCelebration;
  complimentaryTier: PremiumCelebration["tier"] | null;
  onClose: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const upgraded = celebration.previousTier !== null;
  const label = tierLabel(celebration.tier);
  const retainedComplimentaryTier =
    complimentaryTier && tierRank(complimentaryTier) >= tierRank(celebration.tier)
      ? complimentaryTier
      : null;
  const retainedComplimentaryLabel = retainedComplimentaryTier
    ? tierLabel(retainedComplimentaryTier)
    : null;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => {
        const style = window.getComputedStyle(element);
        return (
          element.isConnected &&
          !element.matches(":disabled") &&
          element.getClientRects().length > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      });
      event.preventDefault();
      if (focusable.length === 0) {
        dialogRef.current?.focus();
        return;
      }
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex =
        currentIndex < 0
          ? event.shiftKey
            ? focusable.length - 1
            : 0
          : (currentIndex + direction + focusable.length) % focusable.length;
      focusable[nextIndex].focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="membership-celebration"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="membership-celebration__confetti" aria-hidden="true">
        {Array.from({ length: 18 }, (_, index) => (
          <i
            key={index}
            style={{
              left: `${4 + ((index * 29) % 92)}%`,
              animationDelay: `${(index * 137) % 540}ms`,
              animationDuration: `${1850 + ((index * 173) % 600)}ms`,
            }}
          />
        ))}
      </div>
      <div
        className={`membership-celebration__dialog membership-celebration__dialog--${celebration.tier}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <button
          className="membership-celebration__close"
          ref={closeRef}
          onClick={onClose}
          aria-label="Close membership thank-you"
        >
          <Close />
        </button>

        <div className="membership-celebration__star">
          <TierStar tier={celebration.tier} variant="card" />
        </div>
        <div className="membership-celebration__eyebrow">Wirder premium</div>
        <h2 id={titleId}>
          {upgraded ? <>Thanks for<br />upgrading to {label}</> : "Thank you for joining!"}
        </h2>
        <p id={descriptionId}>
          {retainedComplimentaryLabel
            ? `Your ${label} membership helps keep Wirder free and ad-free. Your complimentary ${retainedComplimentaryLabel} access remains active, so your existing premium extras stay available:`
            : upgraded
              ? <>Your supporter star now<br />reflects your {label} tier.</>
              : `Your ${label} membership helps keep Wirder free and ad-free for everyone. These premium extras are now unlocked:`}
        </p>
        <ul>
          <li>The full friends leaderboard, including scores and found-word lists</li>
          <li>Personal bests broken down by exact word length</li>
          <li>Your {retainedComplimentaryLabel ?? label} supporter star across Wirder</li>
        </ul>
        <button className="btn btn--primary membership-celebration__done" onClick={onClose}>
          Enjoy your extras
        </button>
      </div>
    </div>
  );
}
